import React from 'react';
import { House } from '@material-ui/icons';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import { colors } from '../theme';
import SidebarItem from './Sidebar/SidebarItem';
import { getRole } from '../firebaseAuth';
import UserRoles from '../enums/UserRoles';
import { PrimaryButton } from './Button';

const ADMIN_V2_URL = process.env.REACT_APP_UPK_ADMIN_V2_URL;

export enum AdminV2RedirectType {
  LOCATION = 'location',
  PAYOUTS = 'payouts',
  STRIPE_CONNECT = 'stripe_connect',
  RECONCILIATION = 'reconciliation',
  MANAGE_SERVICES_Categories = 'manage_service_categories'
}

interface AdminV2RedirectProps {
  type: AdminV2RedirectType;
  id?: number;
  inSidebar?: boolean;
  buttonLabel?: string;
  open?: boolean;
  isStripeOnboarding?: boolean;
}

interface RedirectConfig {
  pathname: string;
  text: string;
  icon: JSX.Element;
}

const REDIRECT_CONFIGS: Record<AdminV2RedirectType, RedirectConfig> = {
  [AdminV2RedirectType.LOCATION]: {
    pathname: '/provider/locations',
    text: 'Update address',
    icon: <House style={{color: colors.grey['500']}} />
  },
  [AdminV2RedirectType.PAYOUTS]: {
    pathname: '/payouts/bank-account',
    text: 'Manage payouts',
    icon: <AccountBalanceRoundedIcon style={{color: colors.grey['500']}} />
  },
  [AdminV2RedirectType.STRIPE_CONNECT]: {
    pathname: '/provider/stripe-connect',
    text: 'Manage Stripe Connect',
    icon: <AccountBalanceRoundedIcon style={{color: colors.grey['500']}} />
  },
  [AdminV2RedirectType.RECONCILIATION]: {
    pathname: '/admin/reconciliation',
    text: 'Reconciliation',
    icon: <MonetizationOnOutlinedIcon style={{color: colors.grey['500']}} />
  },
  [AdminV2RedirectType.MANAGE_SERVICES_Categories]: {
    pathname: '/services/manage-categories',
    text: 'Manage Categories',
    icon: <MonetizationOnOutlinedIcon style={{color: colors.grey['500']}} />
  }
};

export const redirectToV2Admin = (type: AdminV2RedirectType, id?: number, isStripeOnboarding?: boolean, isAdmin?: boolean) => {
  const config = REDIRECT_CONFIGS[type];
  const url = new URL(ADMIN_V2_URL);
  url.pathname = config.pathname;

  const token = localStorage.getItem('idToken');

  if (!token) {
    console.error('Authentication token is required. Cannot redirect to admin v2.');
    return;
  }

  if (id && (!isAdmin || type === AdminV2RedirectType.LOCATION)) {
    url.searchParams.append('id', id.toString());

    if (isStripeOnboarding) {
      url.searchParams.append('isStripeOnboarding', '1');
    }
  }

  url.searchParams.append('token', token);
  window.location.href = url.toString();
};

const AdminV2Redirect: React.FC<AdminV2RedirectProps> = ({
  type,
  id,
  inSidebar = false,
  buttonLabel,
  open = false,
  isStripeOnboarding = false,
}) => {
  const config = REDIRECT_CONFIGS[type];
  const role = getRole();
  const isAdmin = role === UserRoles.ADMIN;

  if (inSidebar) {
    return (
      <SidebarItem
        text={config.text}
        open={open}
        isSelected={false}
        icon={config.icon}
        clickHandler={() => redirectToV2Admin(type, id, isStripeOnboarding, isAdmin)}
      />
    );
  }

  return (
    <PrimaryButton
      label={buttonLabel || config.text}
      onClick={() => redirectToV2Admin(type, id, isStripeOnboarding, isAdmin)}
    />
  );
};

export default AdminV2Redirect;
